import { HypotheticalWaterfallPage } from "features/financial-reporting/HypotheticalWaterfallPage";
import {
  getHypotheticalWaterfallNavBreakdownRetrieveLoader,
  getHypotheticalWaterfallPerformanceBreakdownRetrieveLoader,
} from "gen/endpoints/financial-reporting/financial-reporting";
import { getFundListLoader } from "gen/endpoints/fund-entities/fund-entities";
import { getHypotheticalWaterfallOverviewRetrieveLoader } from "gen/endpoints/hypothetical-waterfall/hypothetical-waterfall";
import i18n from "i18n/config";
import { RouteObject } from "react-router-dom";
import { composeLoaders } from "shared/utils/RoutingUtils";

export const financialReportingRoutes: RouteObject = {
  id: "financial-reporting",
  path: "financial-reporting",
  children: [
    {
      id: "index",
      index: true,
      handle: {
        showSidebar: true,
      },
      lazy: async () => {
        const { FinancialReportingPage } = await import(
          "features/financial-reporting/FinancialReportingPage"
        );
        return { Component: FinancialReportingPage };
      },
      loader: composeLoaders(
        getHypotheticalWaterfallOverviewRetrieveLoader(),
        getFundListLoader(),
      ),
    },
    {
      id: "hypoCreate",
      path: "hypo/create",
      lazy: async () => {
        const { HypotheticalWaterfallCreatePage } = await import(
          "features/financial-reporting/HypotheticalWaterfallCreatePage"
        );
        return { Component: HypotheticalWaterfallCreatePage };
      },
    },
    {
      id: "hypoWaterfallEdit",
      path: ":endOfPeriodReportId/edit",
      lazy: async () => {
        const { HypotheticalWaterfallEditPage } = await import(
          "features/financial-reporting/HypotheticalWaterfallEditPage"
        );
        return { Component: HypotheticalWaterfallEditPage };
      },
    },
    {
      id: "hypotheticalWaterfall",
      path: ":endOfPeriodReportId/hypothetical-waterfall/:hypotheticalWaterfallId",
      element: <HypotheticalWaterfallPage />,
      handle: {
        showSidebar: true,
      },
      children: [
        {
          id: "irr",
          path: "performance/irr",
          handle: {
            showSidebar: true,
          },
          lazy: async () => {
            const { HypotheticalWaterfallIRRBreakdown } = await import(
              "features/financial-reporting/HypotheticalWaterfallIRRBreakdown"
            );
            return { Component: HypotheticalWaterfallIRRBreakdown };
          },
        },
        {
          id: "pref-breakdown",
          path: "waterfall-details/:waterfallTierMappingId/fund/:fundEntityId/pref-breakdown",
          handle: {
            showSidebar: true,
          },
          lazy: async () => {
            const { HypotheticalWaterfallPrefFundBreakdown } = await import(
              "features/financial-reporting/HypotheticalWaterfallPrefBreakdown"
            );
            return { Component: HypotheticalWaterfallPrefFundBreakdown };
          },
        },
        {
          id: "investor-pref-breakdown",
          path: "waterfall-details/:waterfallTierMappingId/fund/:fundEntityId/investor-entity/:investorEntityId/pref-breakdown",
          handle: {
            showSidebar: true,
          },
          lazy: async () => {
            const { HypotheticalWaterfallPrefInvestorBreakdown } = await import(
              "features/financial-reporting/HypotheticalWaterfallPrefBreakdown"
            );
            return { Component: HypotheticalWaterfallPrefInvestorBreakdown };
          },
        },
        {
          id: "investor-waterfall-details",
          path: "fund/:fundEntityId/investor-entity/:investorEntityId/waterfall-details",
          handle: {
            showSidebar: true,
          },
          lazy: async () => {
            const { HypotheticalWaterfallInvestorBreakdown } = await import(
              "features/financial-reporting/HypotheticalWaterfallInvestorBreakdown"
            );
            return { Component: HypotheticalWaterfallInvestorBreakdown };
          },
        },
        {
          id: "hypotheticalWaterfallDesignateProtectedCarry",
          path: "designate-protected-carry",
          lazy: async () => {
            const { HypotheticalWaterfallDesignateProtectedCarry } =
              await import(
                "features/financial-reporting/HypotheticalWaterfallDesignateProtectedCarry"
              );
            return { Component: HypotheticalWaterfallDesignateProtectedCarry };
          },
        },
        {
          id: "hypotheticalWaterfallReview",
          path: "review",
          handle: {
            showSidebar: true,
          },
          lazy: async () => {
            const { HypotheticalWaterfallReviewPage } = await import(
              "features/financial-reporting/HypotheticalWaterfallReviewPage"
            );
            return { Component: HypotheticalWaterfallReviewPage };
          },
          children: [
            {
              id: "investorNav",
              path: "investor-nav",
              lazy: async () => {
                const { HypotheticalTabInvestorNav } = await import(
                  "features/financial-reporting/tabs/HypotheticalTabInvestorNav"
                );
                return { Component: HypotheticalTabInvestorNav };
              },
              handle: {
                title: i18n.t("financialReporting:investorNav"),
              },
              loader: composeLoaders(
                getHypotheticalWaterfallNavBreakdownRetrieveLoader(),
              ),
            },
            {
              id: "waterfallDetails",
              path: "waterfall-details",
              lazy: async () => {
                const { HypotheticalTabWaterfallDetails } = await import(
                  "features/financial-reporting/tabs/HypotheticalTabWaterfallDetails"
                );
                return { Component: HypotheticalTabWaterfallDetails };
              },
              handle: {
                title: i18n.t("financialReporting:waterfallDetails"),
              },
            },
            {
              id: "performance",
              path: "performance",
              lazy: async () => {
                const { HypotheticalTabPerformance } = await import(
                  "features/financial-reporting/tabs/HypotheticalTabPerformance"
                );
                return { Component: HypotheticalTabPerformance };
              },
              loader: composeLoaders(
                getHypotheticalWaterfallPerformanceBreakdownRetrieveLoader(),
              ),
              handle: {
                title: i18n.t("financialReporting:performance"),
              },
            },
          ],
        },
      ],
    },
  ],
};
